import { defineStore } from 'pinia'

import { listTranscriptionEntities } from '@/api'
import { errorParser } from '@/helpers'
import type { PageNumberPagination, UUID } from '@/types'
import type { TranscriptionEntity } from '@/types/entity'

import { useNotificationStore } from './notification'

export interface EntityTranscriptionResults extends PageNumberPagination<TranscriptionEntity> {
  loaded: number
}

interface State {
  /**
   * Entities per transcription, with pagination status
   * { [transcription ID]: { count, results, loaded } }
   */
  inTranscription: {
    [transcriptionID: UUID]: EntityTranscriptionResults
  }
}

export const useEntityStore = defineStore('entity', {
  state: (): State => ({
    inTranscription: {},
  }),
  actions: {
    async listInTranscription(transcriptionId: UUID, page = 1) {
      // Init inTranscription on first response
      if (page === 1) delete this.inTranscription[transcriptionId]
      try {
        const data = await listTranscriptionEntities(transcriptionId, { page })

        let current = this.inTranscription[transcriptionId]
        if (!current || current.results === null)
          current = {
            loaded: 0,
            results: [],
            count: data.count,
            previous: null,
            next: null,
            number: 0,
          }
        current.results.push(...data.results)
        current.loaded += data.results.length

        this.inTranscription[transcriptionId] = current

        // Load other pages
        if (data.next) this.listInTranscription(transcriptionId, page + 1)
      } catch (err) {
        useNotificationStore().notify({ type: 'error', text: errorParser(err) })
      }
    },
  },
})
